import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'
import toArray from 'lodash/toArray'
import { computed, makeObservable } from 'mobx'

import { Model } from 'modules/core/lib/mobx-rest'

import { NumericFieldTypes } from 'modules/custom_resources/store/collections/customFields/fields'

export interface Question {
  id: string
  position: number
  category_name?: string
  category_id?: string
  label: string
  type: string
  min_value?: number
  max_value?: number
  options?: string[]
}

export interface Answer {
  question_id: string
  value: string | number
}
export interface Answers {
  [questionId: string]: string | number
}

export interface AnalyticQuestionGroup {
  id: string
  target_id: string
  participant_id: string | null
  answers: Answers
  created_at: string // Date in backend format
  answered_at: string // Date in backend format
  end_on: string // Date in backend format
}

export interface IAnalyticQuestionGroup extends QuestionGroups {
  answers: Answer[]
}

export interface QuestionGroups {
  answered_at: string
  created_at: string
  end_on: string
  id: string
  participant_id: string | null
  respondent_first_name: string
  respondent_last_name: string
}

interface Category {
  name: string
  questionsIds: Set<string>
}

export type Cycle = [string, string] // [start, end], in backend format
const NONE_CATEGORY = 0

class AnalyticResult extends Model {
  constructor(attr = {}) {
    super(attr)
    makeObservable(this, {
      typeOfReviewer: computed,
      questionGroups: computed,
      questions: computed,
      questionIds: computed,
      categories: computed,
      numericQuestions: computed,
      numericQuestionIds: computed,
      numericCategories: computed,
      cycles: computed,
    })
  }

  static urlRoot() {
    return '/surveys/analytics'
  }

  get typeOfReviewer(): string {
    return this.attributes.type_of_reviewer
  }

  get questions(): Set<Question> {
    const groupedQuestions = groupBy(
      this.attributes.questions,
      (q) => q.category_id || NONE_CATEGORY
    )
    const questions = toArray(groupedQuestions).flatMap((questions) =>
      sortBy(questions, 'position')
    )

    return new Set(questions)
  }

  get questionIds(): Set<string> {
    return new Set(Array.from(this.questions).map((q: Question) => q.id))
  }

  get numericQuestions(): Set<Question> {
    const numericFuildTypes = Object.values(NumericFieldTypes)
    const numericQuestions = new Set<Question>()
    this.questions.forEach((question: Question) => {
      if (numericFuildTypes.includes(question.type)) {
        numericQuestions.add(question)
      }
    })

    return numericQuestions
  }

  get numericQuestionIds(): Set<string> {
    return new Set(Array.from(this.numericQuestions).map((q: Question) => q.id))
  }

  get questionGroups(): AnalyticQuestionGroup[] {
    const { question_groups } = this.attributes

    return question_groups.map((questionGroup: IAnalyticQuestionGroup) => {
      const { answers: questionGroupAnswers } = questionGroup

      const answers = questionGroupAnswers.reduce((memo: Answers, answer: Answer) => {
        memo[answer.question_id] = answer.value

        return memo
      }, {})

      return {
        ...questionGroup,
        answers,
      }
    })
  }

  get categories(): Record<string, Category> {
    return this.groupCategories(this.questions)
  }

  get numericCategories(): Record<string, Category> {
    return this.groupCategories(this.numericQuestions)
  }

  groupCategories(questions: Set<Question>): Record<string, Category> {
    const memo: Record<string, Category> = {}

    questions.forEach((question: Question) => {
      const categoryId = question.category_id || NONE_CATEGORY

      if (!memo[categoryId]) {
        memo[categoryId] = {
          name: question.category_name || '',
          questionsIds: new Set([question.id]),
        }
      }

      memo[categoryId]?.questionsIds.add(question.id)
    })

    return memo
  }

  // TODO [people-talent]: cycles need a proper refactor to avoid hacks like this!
  // This is not ideal since we should have cycles modeled better. But since who holds the
  // information of start / end of a question is the question itself, we need to iterate them checking
  // that information
  get cycles(): Set<Cycle> {
    return new Set(
      this.attributes.question_groups.map((questionGroup: AnalyticQuestionGroup) => [
        questionGroup.created_at,
        questionGroup.end_on,
      ])
    )
  }

  questionGroupsInCycle(cycle: Cycle | null): AnalyticQuestionGroup[] {
    if (!cycle) return this.questionGroups

    return this.questionGroups.filter(({ end_on }: AnalyticQuestionGroup) => {
      const [, end] = cycle

      return end_on === end
    })
  }
}

export default AnalyticResult
